import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { motion } from "framer-motion";
import { ArtworkCard } from "../components/ArtworkCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "price-asc", label: "Price: Low to High" },
  { key: "price-desc", label: "Price: High to Low" },
];

export default function ShopPage() {
  const [params] = useSearchParams();
  const type = params.get("type");
  const locked = type === "original" || type === "print";
  const [filter, setFilter] = useState(locked ? type : "all");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    setFilter(locked ? type : "all");
  }, [type, locked]);

  const { data, isLoading } = useQuery({
    queryKey: ["artworks"],
    queryFn: async () => (await axios.get(`${API}/artworks`)).data.artworks,
  });

  const artworks = useMemo(() => {
    let list = data || [];
    if (filter !== "all") list = list.filter((a) => a.type === filter);
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    return list;
  }, [data, filter, sort]);

  return (
    <div className="pt-32 pb-24 px-6 sm:px-12 lg:px-20 min-h-screen" data-testid="shop-page">
      {locked ? (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 text-center"
        >
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#C9501C] capitalize">
            {type === "original" ? "Originals" : "Prints"}
          </h1>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 text-center"
        >
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#C9501C]">
            All works
          </h1>
        </motion.div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12" data-testid="shop-loading">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-[4/5] bg-[#FBF3E2] animate-pulse border border-[rgba(42,33,24,0.06)]" />
          ))}
        </div>
      ) : artworks.length === 0 ? (
        <div className="py-24 text-center" data-testid="shop-empty">
          <p className="font-serif-display italic text-2xl text-[#AB9C86]">
            Coming soon.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12" data-testid="shop-grid">
          {artworks.map((art, i) => (
            <ArtworkCard key={art.id} artwork={art} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
